import { useEffect, useRef, type PointerEvent } from "react";
import { MAX_SIGNATURE_BASE64_LENGTH } from "@froppio/form-core";
import type { FormRendererComponents } from "./types";

export interface SignatureInputProps {
  components: Pick<FormRendererComponents, "Button">;
  id: string;
  label: string;
  describedBy?: string;
  invalid?: boolean;
  onChange: (value: string) => void;
  onError: (error: string | undefined) => void;
}

const INK_WIDTH = 900;
const INK_HEIGHT = 300;

export function SignatureInput({ components, id, label, describedBy, invalid, onChange, onError }: SignatureInputProps) {
  const { Button } = components;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const inkRef = useRef<HTMLCanvasElement | null>(null);
  const pointerRef = useRef<number | null>(null);
  const pointRef = useRef<{ x: number; y: number } | null>(null);

  function repaint() {
    const canvas = canvasRef.current;
    const ink = inkRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !ink || !context) throw new Error("Canvas unavailable");
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(ink, 0, 0, canvas.width, canvas.height);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // A bounded, independent bitmap keeps ink intact when the display canvas resizes.
    const ink = document.createElement("canvas");
    ink.width = INK_WIDTH;
    ink.height = INK_HEIGHT;
    inkRef.current = ink;
    function resize() {
      if (!canvas) return;
      try {
        if (!ink.getContext("2d") || !canvas.getContext("2d")) throw new Error("Canvas unavailable");
        const ratio = Math.min(window.devicePixelRatio || 1, 3);
        canvas.width = Math.max(1, Math.min(1800, Math.round(canvas.clientWidth * ratio)));
        canvas.height = Math.max(1, Math.min(600, Math.round(canvas.clientHeight * ratio)));
        repaint();
      } catch {
        onError("Signature drawing is unavailable in this browser. Try a browser with canvas support.");
      }
    }
    resize();
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(resize);
    observer?.observe(canvas);
    window.addEventListener("resize", resize);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", resize);
      inkRef.current = null;
      pointerRef.current = null;
      pointRef.current = null;
    };
  }, [onError]);

  function draw(event: PointerEvent<HTMLCanvasElement>, start: boolean) {
    const ink = inkRef.current;
    const context = ink?.getContext("2d");
    if (!ink || !context) throw new Error("Canvas unavailable");
    const bounds = event.currentTarget.getBoundingClientRect();
    if (!bounds.width || !bounds.height) throw new Error("Canvas has no size");
    const point = {
      x: Math.max(0, Math.min(INK_WIDTH, (event.clientX - bounds.left) * INK_WIDTH / bounds.width)),
      y: Math.max(0, Math.min(INK_HEIGHT, (event.clientY - bounds.top) * INK_HEIGHT / bounds.height)),
    };
    const inkColor = window.getComputedStyle(event.currentTarget).color;
    context.strokeStyle = inkColor;
    context.fillStyle = inkColor;
    context.lineWidth = 3;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.beginPath();
    const previous = pointRef.current;
    if (start || !previous) {
      context.arc(point.x, point.y, 1.5, 0, Math.PI * 2);
      context.fill();
    } else {
      context.moveTo(previous.x, previous.y);
      context.lineTo(point.x, point.y);
      context.stroke();
    }
    pointRef.current = point;
    repaint();
  }

  function drawingFailed() {
    pointerRef.current = null;
    pointRef.current = null;
    onChange("");
    onError("The signature could not be drawn. Clear it and try again.");
  }

  function startDrawing(event: PointerEvent<HTMLCanvasElement>) {
    if (pointerRef.current !== null || !event.isPrimary || event.button !== 0) return;
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
      pointerRef.current = event.pointerId;
      onChange("");
      onError("Finish drawing the signature before checking answers.");
      draw(event, true);
    } catch {
      drawingFailed();
    }
  }

  function moveDrawing(event: PointerEvent<HTMLCanvasElement>) {
    if (pointerRef.current !== event.pointerId) return;
    event.preventDefault();
    try {
      draw(event, false);
    } catch {
      drawingFailed();
    }
  }

  function finishDrawing(event: PointerEvent<HTMLCanvasElement>) {
    if (pointerRef.current !== event.pointerId) return;
    try {
      if (event.type === "pointerup") draw(event, false);
      pointerRef.current = null;
      pointRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      const dataUrl = inkRef.current?.toDataURL("image/png");
      const prefix = "data:image/png;base64,";
      if (!dataUrl?.startsWith(prefix)) throw new Error("PNG export unavailable");
      const base64 = dataUrl.slice(prefix.length);
      if (!base64 || base64.length > MAX_SIGNATURE_BASE64_LENGTH) throw new Error("PNG exceeds size limit");
      onChange(base64);
      onError(undefined);
    } catch {
      pointerRef.current = null;
      pointRef.current = null;
      onChange("");
      onError("The signature could not be exported as a PNG within the size limit. Clear it and try again.");
    }
  }

  function clear() {
    pointerRef.current = null;
    pointRef.current = null;
    onChange("");
    try {
      const context = inkRef.current?.getContext("2d");
      if (!context) throw new Error("Canvas unavailable");
      context.clearRect(0, 0, INK_WIDTH, INK_HEIGHT);
      repaint();
      onError(undefined);
    } catch {
      onError("Signature drawing is unavailable in this browser. Try a browser with canvas support.");
    }
  }

  return (
    <div className="space-y-3">
      <p id={`${id}-instructions`} className="text-xs leading-relaxed text-muted-foreground">
        Draw with a mouse, touch, or stylus. Keyboard drawing is not supported. You can clear the drawing below.
        This preview keeps the signature in memory only.
      </p>
      <canvas
        ref={canvasRef}
        id={id}
        role="img"
        aria-label={`${label}: signature drawing area`}
        aria-describedby={[`${id}-instructions`, describedBy].filter(Boolean).join(" ")}
        aria-invalid={invalid || undefined}
        className="block aspect-[3/1] w-full touch-none rounded-xl border border-input bg-card text-foreground aria-invalid:border-destructive"
        onPointerDown={startDrawing}
        onPointerMove={moveDrawing}
        onPointerUp={finishDrawing}
        onPointerCancel={finishDrawing}
        onLostPointerCapture={finishDrawing}
      >
        Signature drawing requires a browser with canvas support.
      </canvas>
      <Button type="button" variant="outline" size="sm" onClick={clear} aria-label={`Clear signature for ${label}`}>Clear signature</Button>
    </div>
  );
}
